/**
 * @file Resolves the API sandbox URL for an organization and its public key audience.
 */

import { getConfig } from "@components/Config";
import { type OrganizationPublicKeyAudience } from "@api/types";

import { AUDIENCE_BACKSTAGE } from "./audiences";

export type ApiSandbox = {
  investorUrl?: string;
  backstageUrl?: string;
};

type Props = {
  audience: OrganizationPublicKeyAudience;
  organizationId?: string;
};

/**
 * Resolves the sandbox URL of an organization for the given audience.
 */
const getApiSandboxUrl = ({
  audience,
  organizationId,
}: Props): string | undefined => {
  const sandboxes = getConfig("API_SANDBOXES") as Record<string, ApiSandbox>;
  const hasOrganizationId = Boolean(organizationId);

  if (!hasOrganizationId) {
    return undefined;
  }

  const sandbox = sandboxes[organizationId];
  const hasSandbox = !!sandbox;

  if (!hasSandbox) {
    return undefined;
  }

  const isBackstageAudience = audience === AUDIENCE_BACKSTAGE;

  const url = isBackstageAudience
    ? sandbox.backstageUrl
    : sandbox.investorUrl;

  return url;
};

export default getApiSandboxUrl;
